/**
 * ATL-1440 — Settings → API tokens. The app's side of `/api-tokens` (admins only): issue, list and
 * revoke the organization tokens agents use with Atlas API v1, and read what those agents did.
 *
 * Responses are checked for the fields the screen depends on and refused loudly when they are
 * missing, rather than rendered as an empty list that would read as "no tokens".
 */
import type { AxiosInstance } from 'axios'
import apiClient from '../API/apiClient'
import { isAiClient, type AiClient } from '../AiAssistants/aiAssistantsApi'

export type ApiTokenStatus = 'active' | 'expired' | 'revoked'

/**
 * ATL-1694: `manual` is a token an admin issued on this screen; `oauth` is a Claude / ChatGPT
 * connection an admin approved on the consent screen. A backend older than ATL-1694 sends neither
 * field — it could only issue manual tokens, so its tokens are read as `manual` with no client.
 */
export type ApiTokenKind = 'manual' | 'oauth'

export interface ApiToken {
  id: string
  name: string
  tokenPrefix: string
  scopes: string[]
  status: ApiTokenStatus
  createdAt: string
  expiresAt: string
  lastUsedAt: string | null
  revokedAt: string | null
  /** `firebaseUid` is the stable identity; `id` (users.id) and `name` are as they were at issue. */
  createdBy: ApiTokenPerson
  revokedBy: ApiTokenPerson | null
  kind: ApiTokenKind
  /** The assistant an `oauth` connection was approved for; `null` for a manual token. */
  client: AiClient | null
}

export interface ApiTokenPerson {
  id: string
  firebaseUid: string | null
  name: string | null
}

export interface ApiTokensAvailability {
  available: boolean
  reason: 'organization_not_on_supabase' | 'organization_access_undetermined' | null
}

export interface ApiTokensList {
  availability: ApiTokensAvailability
  issuableScopes: string[]
  allScopes: string[]
  tokens: ApiToken[]
}

export interface ApiAuditEvent {
  id: string
  occurredAt: string
  actorType: 'user' | 'agent'
  channel: 'app' | 'api' | 'mcp'
  action: string
  targetType: string | null
  targetId: string | null
  outcome: 'success' | 'denied' | 'error'
  httpStatus: number | null
  user: ApiTokenPerson | null
  token: { id: string; name: string; tokenPrefix: string } | null
}

export interface CreateApiTokenInput {
  name: string
  scopes: string[]
  expiresInDays: number
}

const isPerson = (p: unknown): p is ApiTokenPerson =>
  !!p && typeof p === 'object' && typeof (p as ApiTokenPerson).id === 'string'

type WireToken = Omit<ApiToken, 'kind' | 'client'> & { kind?: unknown; client?: unknown }

const isWireToken = (t: unknown): t is WireToken =>
  !!t &&
  typeof (t as WireToken).id === 'string' &&
  typeof (t as WireToken).name === 'string' &&
  typeof (t as WireToken).status === 'string' &&
  Array.isArray((t as WireToken).scopes) &&
  // The screen renders `createdBy.name`; a missing person must be refused here, not crash there.
  isPerson((t as WireToken).createdBy)

const tokenOrThrow = (t: unknown): ApiToken => {
  if (!isWireToken(t)) throw new Error('api token response is malformed')
  const { kind, client, ...rest } = t
  // Absent on a pre-ATL-1694 backend, which had no other kind (see ApiTokenKind).
  if (kind === undefined && client === undefined) return { ...rest, kind: 'manual', client: null }
  if (kind === 'manual' && (client === null || client === undefined)) return { ...rest, kind: 'manual', client: null }
  // An OAuth connection without its client would render as an anonymous token: refuse it.
  if (kind === 'oauth' && isAiClient(client)) return { ...rest, kind: 'oauth', client }
  throw new Error('api token response is malformed')
}

export const createApiTokensApi = (http: AxiosInstance) => ({
  async list(): Promise<ApiTokensList> {
    const { data } = await http.get('/api-tokens')
    const d = data as Partial<ApiTokensList> | null
    if (!d || !d.availability || !Array.isArray(d.tokens) || !Array.isArray(d.issuableScopes)) {
      throw new Error('api tokens response is malformed')
    }
    return {
      availability: d.availability,
      issuableScopes: d.issuableScopes,
      allScopes: Array.isArray(d.allScopes) ? d.allScopes : d.issuableScopes,
      tokens: d.tokens.map(tokenOrThrow),
    }
  },

  /** The secret is in this response only. */
  async create(input: CreateApiTokenInput): Promise<{ token: ApiToken; secret: string }> {
    const { data } = await http.post('/api-tokens', input)
    const d = data as { token?: unknown; secret?: unknown } | null
    if (!d || typeof d.secret !== 'string' || !d.secret.startsWith('atl_')) {
      throw new Error('api token create response is malformed')
    }
    return { token: tokenOrThrow(d.token), secret: d.secret }
  },

  async revoke(tokenId: string): Promise<ApiToken> {
    const { data } = await http.post(`/api-tokens/${encodeURIComponent(tokenId)}/revoke`, {})
    return tokenOrThrow((data as { token?: unknown } | null)?.token)
  },

  /** `before` is the last event of the previous page (see `nextAuditCursor`). */
  async audit(
    opts: { limit?: number; before?: { occurredAt: string; id: string } | null; tokenId?: string } = {}
  ): Promise<ApiAuditEvent[]> {
    const params: Record<string, string | number> = {}
    if (opts.limit !== undefined) params.limit = opts.limit
    if (opts.tokenId !== undefined) params.tokenId = opts.tokenId
    if (opts.before) {
      params.before = opts.before.occurredAt
      params.beforeId = opts.before.id
    }
    const { data } = await http.get('/api-tokens/audit', { params })
    const items = (data as { items?: unknown } | null)?.items
    if (!Array.isArray(items)) throw new Error('api audit response is malformed')
    return items as ApiAuditEvent[]
  },
})

/**
 * The backend's refusal code, when the error is one. Errors arrive in the global envelope
 * `{ statusCode, message: { code, message } }`.
 */
export const apiTokensErrorCode = (error: unknown): string | null => {
  const body = (error as { response?: { data?: { message?: unknown } } })?.response?.data
  const message = body?.message as { code?: unknown } | undefined
  return typeof message?.code === 'string' ? message.code : null
}

/** Production instance bound to the shared authenticated axios client. */
export const apiTokensApi = createApiTokensApi(apiClient)
export type ApiTokensApi = ReturnType<typeof createApiTokensApi>
